import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  collection,
  addDoc,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const PrequalSignup = () => {
  const { prequalId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const [prequalData, setPrequalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [showCalendlyPopup, setShowCalendlyPopup] = useState(false);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');

  // Fetch prequalification data
  useEffect(() => {
    const fetchPrequalData = async () => {
      if (!prequalId) {
        setError('Invalid link');
        setLoading(false);
        return;
      }

      try {
        const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
        const prequalDocSnap = await getDoc(prequalDocRef);

        if (!prequalDocSnap.exists()) {
          setError('This prequalification link is no longer valid');
          setLoading(false);
          return;
        }

        const data = { id: prequalDocSnap.id, ...prequalDocSnap.data() };
        console.log('Fetched prequal data:', data);
        console.log('Documents in prequal:', data.documents);
        setPrequalData(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching prequalification:', err);
        setError('Failed to load prequalification data');
        setLoading(false);
      }
    };

    fetchPrequalData();
  }, [prequalId]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!isExistingUser && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!isExistingUser && password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let user;

      if (isExistingUser) {
        // Sign in existing user
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        user = userCredential.user;
      } else {
        // Create new user
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        user = userCredential.user;

        // Create user profile in 'users' collection (same as normal signup)
        const userProfileRef = doc(db, 'users', user.uid);
        await setDoc(userProfileRef, {
          email: user.email,
          firstName,
          lastName,
          phoneNumber: phone || '',
          status: 'Pre-lender',
          lastUserUpdate: new Date(),
          fromPrequalification: true,
          prequalificationId: prequalId
        });
      }

      // Create the business data from prequalification
      // Ensure no undefined values (Firebase doesn't allow them)
      const businessData = {
        businessName: prequalData.businessName || 'Prequalified Business',
        loanSize: prequalData.purchasePrice || prequalData.prequalifiedAmount || 0,
        businessState: prequalData.businessState || '',
        businessAge: '2+ Years', // Default assumption for prequalified businesses
        purchaseType: 'Asset',
        prequalified: true,
        prequalificationId: prequalId,
        prequalificationData: {
          amount: prequalData.prequalifiedAmount || 0,
          type: prequalData.prequalificationType || 'Standard Financing',
          withSalary: prequalData.prequalificationWithSalary || false
        },
        businessSummary: prequalData.businessDescription || '',
        loanapplicationstatus: 'in progress',
        currentStep: 0,
        createdAt: new Date(),
        lastUpdated: new Date()
      };

      // Create loan application with the prequalified business
      // Filter out any undefined values
      const loanApplicationData = {
        userId: user.uid,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        loanPurpose: 'Acquisition', // Always Acquisition for prequal signups (searchers buying businesses)
        firstName: firstName || '',
        lastName: lastName || '',
        emails: [email],
        phoneNumbers: phone ? [phone] : [],
        personalGuarantors: [{ firstName: firstName || '', lastName: lastName || '' }],
        businesses: [businessData],
        // Also store at root level for backwards compatibility
        ...businessData,
        fromPrequalification: true,
        prequalificationId: prequalId,
        brokerId: prequalData.brokerId || null
      };

      // Create the loan application
      const loanAppRef = await addDoc(collection(db, 'loanApplications'), loanApplicationData);

      // Copy documents from BOTH places (and deduplicate):
      // 1. Old format: documents array directly on prequalificationApplications document
      // 2. New format: prequalificationMetadata collection

      // Map broker labels to customer portal labels
      const labelMapping = {
        '2024 Business Tax Return': 'Business Tax Return - Current Year',
        '2023 Business Tax Return': 'Business Tax Return - Last Year',
        '2022 Business Tax Return': 'Business Tax Return - 2 Years Ago',
        '2025 YTD P&L': 'Interim P&L',
        '2025 YTD Balance Sheet': 'Balance Sheet',
        'CIM Document': 'CIM',
      };

      const mapLabel = (brokerLabel) => {
        // Direct match first
        if (labelMapping[brokerLabel]) {
          return labelMapping[brokerLabel];
        }
        // Also check with P+L instead of P&L
        const altLabel = brokerLabel?.replace('P+L', 'P&L');
        if (altLabel && labelMapping[altLabel]) {
          return labelMapping[altLabel];
        }
        // Check for P&L/PnL variations (handle different formats)
        if (brokerLabel) {
          const lower = brokerLabel.toLowerCase();
          if (lower.includes('p&l') || lower.includes('pnl') || lower.includes('p+l') ||
              lower.includes('profit') || lower.includes('loss')) {
            if (lower.includes('ytd') || lower.includes('interim') || lower.includes('2025') || lower.includes('2024')) {
              return 'Interim P&L';
            }
          }
        }
        // Return original label or default to 'Other' if undefined/null
        return brokerLabel || 'Other';
      };

      let allPrequalDocs = [];
      const seenFileUrls = new Set(); // For deduplication

      // Check documents array on the prequal document (old format)
      if (prequalData.documents && prequalData.documents.length > 0) {
        console.log('Found documents in prequal document:', prequalData.documents.length);
        prequalData.documents.forEach(doc => {
          if (doc.fileUrl && !seenFileUrls.has(doc.fileUrl)) {
            seenFileUrls.add(doc.fileUrl);
            allPrequalDocs.push(doc);
          }
        });
      }

      // Also check prequalificationMetadata collection (new format)
      const prequalMetadataQuery = query(
        collection(db, 'prequalificationMetadata'),
        where('applicationId', '==', prequalId)
      );
      const prequalMetadataSnapshot = await getDocs(prequalMetadataQuery);
      console.log('Found documents in prequalificationMetadata collection:', prequalMetadataSnapshot.size);

      prequalMetadataSnapshot.docs.forEach(docSnap => {
        const docData = docSnap.data();
        // Only add if not already seen (deduplicate by fileUrl)
        if (docData.fileUrl && !seenFileUrls.has(docData.fileUrl)) {
          seenFileUrls.add(docData.fileUrl);
          allPrequalDocs.push(docData);
        }
      });

      console.log('Total unique documents to copy:', allPrequalDocs.length);

      if (allPrequalDocs.length > 0) {
        console.log('Copying documents to loan application:', loanAppRef.id);
        const documentPromises = [];

        for (const docData of allPrequalDocs) {
          console.log('Processing doc:', docData.label, 'status:', docData.status);

          // Skip documents marked as 'not_available'
          if (docData.status === 'not_available') {
            console.log('Skipping not_available doc:', docData.label);
            continue;
          }

          // Skip if no fileUrl (required field)
          if (!docData.fileUrl) {
            console.log('Skipping doc without fileUrl:', docData.label);
            continue;
          }

          // Create a new document metadata entry for the borrower's loan application
          // Map broker labels to customer portal labels
          const mappedLabel = mapLabel(docData.label);
          console.log('Mapping label:', docData.label, '->', mappedLabel);

          const newDocMetadata = {
            fileName: docData.fileName || 'Document',
            fileUrl: docData.fileUrl,
            uploadedAt: docData.uploadedAt || new Date().toISOString(),
            userId: user.uid,
            documentName: docData.documentName || 'Business Document',
            loanApplicationId: loanAppRef.id,
            label: mappedLabel,
            businessIndex: 0, // First business
            ownerId: null, // Not a personal document
            copiedFromPrequal: true,
            prequalificationId: prequalId,
            originalBrokerId: docData.brokerId || docData.userId || null
          };

          console.log('Creating doc metadata:', newDocMetadata);
          documentPromises.push(
            addDoc(collection(db, 'documentMetadata'), newDocMetadata)
              .then(docRef => {
                console.log('Created doc with ID:', docRef.id);
                return docRef;
              })
              .catch(err => {
                console.error('Error creating doc:', err);
                return null;
              })
          );
        }

        const results = await Promise.all(documentPromises);
        const successCount = results.filter(r => r !== null).length;
        console.log('Successfully copied', successCount, 'of', documentPromises.length, 'documents');
      } else {
        console.log('No documents found to copy');
      }

      // Add borrower to prequalification's borrowers array
      // Note: Can't use serverTimestamp() inside arrayUnion(), so use Date
      const borrowerData = {
        id: user.uid,
        firstName,
        lastName,
        email,
        phone: phone || null,
        loanApplicationId: loanAppRef.id,
        signedUpAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'prequalificationApplications', prequalId), {
        borrowers: arrayUnion(borrowerData)
      });

      // Show Calendly popup instead of navigating directly
      setShowCalendlyPopup(true);
    } catch (err) {
      console.error('Signup error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Try logging in instead.');
        setIsExistingUser(true);
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up.');
        setIsExistingUser(false);
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error && !prequalData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">{error}</h2>
          <p className="mt-2 text-sm text-gray-500">
            Please contact the broker who shared this link with you.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Left Panel - Prequalification Info */}
          <div className="w-full md:w-1/2 bg-gradient-to-br from-indigo-600 to-indigo-800 p-8 text-white">
            <div className="mb-8">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-400 text-green-900">
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                Pre-Qualified
              </span>
            </div>

            <h2 className="text-3xl font-bold mb-2">
              {prequalData?.businessName || 'Business Acquisition'}
            </h2>

            <p className="text-indigo-200 mb-8">
              {prequalData?.businessDescription || 'You\'ve been invited to apply for financing on this prequalified business.'}
            </p>

            <div className="space-y-4">
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm text-indigo-200">Prequalified Amount</span>
                </div>
                <p className="text-3xl font-bold">
                  {formatCurrency(prequalData?.prequalifiedAmount)}
                </p>
              </div>

              {prequalData?.businessState && (
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                    <span className="text-sm text-indigo-200">Location</span>
                  </div>
                  <p className="text-lg font-semibold">
                    {prequalData.businessCity && `${prequalData.businessCity}, `}
                    {prequalData.businessState}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-4">
                {prequalData?.prequalificationType && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/20">
                    {prequalData.prequalificationType}
                  </span>
                )}
                {prequalData?.prequalificationWithSalary && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/20">
                    With $75k Salary
                  </span>
                )}
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/20">
                  SBA Loan
                </span>
              </div>
            </div>
          </div>

          {/* Right Panel - Signup Form */}
          <div className="w-full md:w-1/2 p-8">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900">
                {isExistingUser ? 'Sign In' : 'Create Your Account'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {isExistingUser
                  ? 'Sign in to add this listing to your account'
                  : 'Sign up to start your loan application'}
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSignup} className="space-y-4">
              {!isExistingUser && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {!isExistingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {!isExistingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
              >
                {isSubmitting
                  ? 'Processing...'
                  : isExistingUser
                    ? 'Sign In & Add Listing'
                    : 'Create Account & Continue'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsExistingUser(!isExistingUser);
                    setError(null);
                  }}
                  className="text-sm text-indigo-600 hover:text-indigo-700"
                >
                  {isExistingUser
                    ? "Don't have an account? Sign up"
                    : 'Already have an account? Sign in'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Calendly Popup */}
      {showCalendlyPopup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-3xl w-full mx-4" style={{ maxHeight: '90vh' }}>
            <h2 className="text-xl font-bold mb-2 text-center">Account Created Successfully!</h2>
            <p className="text-center text-gray-600 mb-4">
              Book a free consultation to discuss your SBA loan needs with our team.
            </p>
            <div className="w-full" style={{ height: '500px' }}>
              <iframe
                src="https://calendly.com/isak-lended/lended-sba-financing-intro-call"
                width="100%"
                height="100%"
                frameBorder="0"
                title="Schedule a consultation"
              ></iframe>
            </div>
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => navigate('/portal', { state: { retryCount: 0 } })}
                className="text-gray-600 hover:text-gray-800 underline text-sm"
              >
                Skip for now and go to Portal →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrequalSignup;
